import { useCallback, useEffect, useRef, useState } from "react";
import ContainerCustom from "../../../Components/ContainerCustom";
import Table from "../../../Components/Table/Table";
import TableButton from "../../../Components/Buttons/TableButton";
import StatusColorful from "../../../Components/Table/Components/StatusColorful";
import { isoToJalali } from "../../../helper/isoToJalali";
import { API_BASE_URL } from "../../../config/api";

const ACCOUNTS_ENDPOINT =
  `${API_BASE_URL}/api/v0/challenges/admin/accounts/`;
const REFRESH_ENDPOINT =
  `${API_BASE_URL}/api/v0/auth/admin/jwt/refresh/`;

// Backend filter values currently follow the API's numeric status ids.
const ACCOUNT_FILTER_QUERY_MAP = [1, 2, 3, 4];
const ACCOUNT_RESPONSE_GROUPS = [
  { key: "live", filterIndex: 0, status: "active" },
  { key: "inactive", filterIndex: 1, status: "deactive" },
  { key: "passed", filterIndex: 2, status: "done" },
  { key: "failed", filterIndex: 3, status: "failed" },
];

function logoutAndRedirect() {
  localStorage.removeItem("access");
  localStorage.removeItem("authCredentials");
  localStorage.removeItem("refresh");
  localStorage.removeItem("role");
  localStorage.removeItem("email");
  window.location.href = "/signin";
}

async function authorizedGet(url) {
  let response = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${localStorage.getItem("access")}`,
    },
  });

  if (response.status === 401) {
    const refreshResponse = await fetch(REFRESH_ENDPOINT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        refresh: localStorage.getItem("refresh"),
      }),
    });

    if (!refreshResponse.ok) {
      logoutAndRedirect();
      return null;
    }

    const refreshData = await refreshResponse.json();
    localStorage.setItem("access", refreshData.access);

    response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("access")}`,
      },
    });
  }

  if (!response.ok) {
    throw new Error(`HTTP error! Status: ${response.status}`);
  }

  return response.json();
}

function getSelectedBackendFilters(activeFilter) {
  return activeFilter.flatMap((isActive, index) =>
    isActive && ACCOUNT_FILTER_QUERY_MAP[index] !== undefined
      ? [ACCOUNT_FILTER_QUERY_MAP[index]]
      : [],
  );
}

function buildAccountsUrl({ page, activeFilter, searchTerm }) {
  const params = new URLSearchParams();
  const selectedFilters = getSelectedBackendFilters(activeFilter);

  params.set("page", String(page));
  params.set("filter", selectedFilters.join(","));

  if (searchTerm) {
    params.set("search", searchTerm);
  }

  return `${ACCOUNTS_ENDPOINT}?${params.toString()}`;
}

function normalizeStatus(status) {
  const value = String(status || "").toLowerCase();

  if (value === "live" || value === "active") return "active";
  if (value === "inactive" || value === "deactive") return "deactive";
  if (value === "passed" || value === "done" || value === "completed") {
    return "done";
  }
  if (value === "failed" || value === "fail") return "failed";

  return value;
}

function normalizePhase(phaseType) {
  if (phaseType === "phase1") return "فاز ۱";
  if (phaseType === "phase2") return "فاز ۲";
  if (phaseType === "funded") return "فاندد";
  return "-";
}

function normalizeChallengeType(challengeType) {
  if (!challengeType) return "-";
  return challengeType === "real" ? "واقعی" : "تمرینی";
}

function getUserName(item) {
  if (item.user_fn && item.user_ln) {
    return `${item.user_fn} ${item.user_ln}`;
  }

  if (item.user?.fn && item.user?.ln) {
    return `${item.user.fn} ${item.user.ln}`;
  }

  return (
    item.user_full_name ??
    item.user_email ??
    item.user?.email ??
    item.user?.full_name ??
    "-"
  );
}

function mapAccountRow(item, fallbackStatus = "") {
  const challenge = item.challenge ?? {};
  const phase = item.phase ?? {};
  const account = item.account ?? {};
  const createdAt = item.created_at ?? account.created_at;

  return {
    id: item.id ?? account.id ?? item.code ?? "-",
    uuid: item.code ?? account.code ?? "-",
    date: createdAt ? isoToJalali(createdAt) : "-",
    un: getUserName(item),
    type: normalizeChallengeType(
      item.challenge_type ?? challenge.challenge_type ?? challenge.type,
    ),
    cluster: item.challenge_category ?? challenge.category ?? "-",
    name: item.challenge_name ?? challenge.name ?? "-",
    phase: normalizePhase(item.phase_type ?? phase.phase_type),
    status: normalizeStatus(item.status ?? account.status ?? fallbackStatus),
    detail: "",
  };
}

function mapAccountsResponse(data, activeFilter) {
  return ACCOUNT_RESPONSE_GROUPS.flatMap(({ key, filterIndex, status }) => {
    if (!activeFilter[filterIndex] || !Array.isArray(data?.[key])) {
      return [];
    }

    return data[key].map((item) => mapAccountRow(item, status));
  });
}

function mergeRows(previousRows, nextRows) {
  const seen = new Set(previousRows.map((row) => row.uuid));
  const uniqueRows = nextRows.filter((row) => {
    if (seen.has(row.uuid)) return false;
    seen.add(row.uuid);
    return true;
  });

  return [...previousRows, ...uniqueRows];
}

export default function AccountsList() {
  const [activeFilter, setActiveFilter] = useState([true, true, true, true]);
  const [searchState, setSearchState] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [tableData, setTableData] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    count: 0,
    pageSize: 0,
    totalPages: 1,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState("");

  const requestIdRef = useRef(0);
  const loadMoreRef = useRef(null);

  const fetchAccountData = useCallback(
    async ({ page = 1, replace = false, searchTerm }) => {
      const selectedFilters = getSelectedBackendFilters(activeFilter);

      if (selectedFilters.length === 0) {
        setTableData([]);
        setPagination({
          page: 1,
          count: 0,
          pageSize: 0,
          totalPages: 0,
        });
        setIsLoading(false);
        setIsLoadingMore(false);
        setError("");
        return;
      }

      const requestId = requestIdRef.current + 1;
      requestIdRef.current = requestId;

      setError("");

      if (replace) {
        setIsLoading(true);
        setIsLoadingMore(false);
      } else {
        setIsLoadingMore(true);
      }

      try {
        const data = await authorizedGet(
          buildAccountsUrl({
            page,
            activeFilter,
            searchTerm,
          }),
        );

        if (!data || requestId !== requestIdRef.current) {
          return;
        }

        const mappedRows = mapAccountsResponse(data, activeFilter);

        setTableData((previousRows) =>
          replace ? mappedRows : mergeRows(previousRows, mappedRows),
        );
        setPagination({
          page: data.page ?? page,
          count: data.count ?? mappedRows.length,
          pageSize: data.page_size ?? mappedRows.length,
          totalPages: data.total_pages ?? page,
        });
      } catch (fetchError) {
        if (requestId !== requestIdRef.current) {
          return;
        }

        console.error("Error fetching account data:", fetchError);
        setError("خطا در دریافت لیست حساب‌ها");

        if (replace) {
          setTableData([]);
        }
      } finally {
        if (requestId === requestIdRef.current) {
          setIsLoading(false);
          setIsLoadingMore(false);
        }
      }
    },
    [activeFilter],
  );

  useEffect(() => {
    fetchAccountData({
      page: 1,
      replace: true,
      searchTerm: appliedSearch,
    });
  }, [appliedSearch, fetchAccountData]);

  useEffect(() => {
    const target = loadMoreRef.current;

    if (!target) {
      return;
    }

    const scrollContainer = target.closest(".overflow-auto");
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];

        if (
          !entry?.isIntersecting ||
          isLoading ||
          isLoadingMore ||
          pagination.page >= pagination.totalPages ||
          pagination.totalPages === 0
        ) {
          return;
        }

        fetchAccountData({
          page: pagination.page + 1,
          replace: false,
          searchTerm: appliedSearch,
        });
      },
      {
        root: scrollContainer,
        rootMargin: "200px 0px",
      },
    );

    observer.observe(target);

    return () => {
      observer.disconnect();
    };
  }, [
    appliedSearch,
    fetchAccountData,
    isLoading,
    isLoadingMore,
    pagination.page,
    pagination.totalPages,
  ]);

  const handleSearchClick = () => {
    const nextSearch = searchState.trim();

    if (nextSearch === appliedSearch) {
      fetchAccountData({
        page: 1,
        replace: true,
        searchTerm: nextSearch,
      });
      return;
    }

    setAppliedSearch(nextSearch);
  };

  return (
    <ContainerCustom
      header="لیست حساب‌ها"
      headSVG="archive"
      colorfulFilterOptions={[
        {
          SVGName: "tick",
          text: "فعال",
          className: "w-24",
          color: "blue",
        },
        {
          SVGName: "stall",
          text: "غیر فعال",
          className: "w-24",
          color: "yellow",
        },
        {
          SVGName: "star",
          text: "تکمیل شده",
          className: "w-24",
          color: "green",
        },
        {
          SVGName: "failed",
          text: "رد شده",
          className: "w-24",
          color: "red",
        },
      ]}
      activeFilter={activeFilter}
      setActiveFilter={setActiveFilter}
      filter={false}
      sort={false}
      search={true}
      searchValue={searchState}
      setSearchState={setSearchState}
      searchClick={handleSearchClick}
    >
      <div className="flex flex-col">
        <Table
          columns={[
            { key: "uuid", label: "کد حساب" },
            { key: "date", label: "تاریخ ایجاد" },
            { key: "un", label: "نام کاربر" },
            { key: "type", label: "نوع چالش" },
            { key: "cluster", label: "دسته چالش" },
            { key: "name", label: "نام چالش" },
            { key: "phase", label: "نوع فاز" },
            {
              key: "status",
              label: "وضعیت",
              render: (value) =>
                value === "active" ? (
                  <StatusColorful
                    SVGName="tick"
                    text="فعال"
                    className="w-24"
                    color="blue"
                  />
                ) : value === "deactive" ? (
                  <StatusColorful
                    SVGName="stall"
                    text="غیر فعال"
                    className="w-24"
                    color="yellow"
                  />
                ) : value === "done" ? (
                  <StatusColorful
                    SVGName="star"
                    text="تکمیل شده"
                    className="w-24"
                    color="green"
                  />
                ) : value === "failed" ? (
                  <StatusColorful
                    SVGName="failed"
                    text="رد شده"
                    className="w-24"
                    color="red"
                  />
                ) : (
                  "-"
                ),
            },
            {
              key: "detail",
              label: "جزئیات",
              render: (value, row) => (
                <TableButton
                  text="مشاهده"
                  href={`/account/${row.uuid}/overview`}
                  className="!py-1 !px-2 !rounded-md bg-zi-bg1-light dark:bg-zi-bg1-dark text-zi-text-light dark:text-zi-text-dark hocus:ring-1 hocus:ring-inset hocus:ring-zi-border-dark"
                />
              ),
            },
          ]}
          data={tableData}
        />
        {error && (
          <div className="pt-4 text-center text-sm text-red-500">{error}</div>
        )}
        {!error && !isLoading && tableData.length === 0 && (
          <div className="pt-4 text-center text-sm text-zi-text-light/50 dark:text-zi-text-dark/50">
            نتیجه‌ای یافت نشد
          </div>
        )}
        <div
          ref={loadMoreRef}
          className="pt-4 text-center text-sm text-zi-text-light/50 dark:text-zi-text-dark/50"
        >
          {isLoading || isLoadingMore ? "در حال بارگذاری..." : ""}
        </div>
      </div>
    </ContainerCustom>
  );
}
